
import {
  cellsToRanges,
  localDayKey,
  rangesToCells,
  type CellKey,
  type InstantRange,
} from '@client/shared/calendar/slot-grid';

export interface ReasonedRange extends InstantRange {
  readonly reason?: string;
}

export function mergeReasonedRanges(
  previous: readonly ReasonedRange[],
  painted: ReadonlySet<CellKey>,
  addedReason: string | undefined,
  visibleDays: readonly string[],
  tz: string,
): ReasonedRange[] {
  const visible = new Set(visibleDays);

  const retained = previous.filter((r) => !visible.has(localDayKey(r.start, tz)));

  const committedCells = rangesToCells(previous, visibleDays, tz);
  const reasonByCell = committedReasonByCell(previous, visibleDays, tz);

  const derived = cellsToRanges(painted, visibleDays, tz).map<ReasonedRange>((r) => {
    const cells = rangeCellKeys(r, visibleDays, tz);
    const hasNew = cells.some((k) => !committedCells.has(k));
    if (hasNew) {
      return addedReason === undefined ? { start: r.start, end: r.end } : { ...r, reason: addedReason };
    }
    const inherited = cells.map((k) => reasonByCell.get(k)).find((v) => v !== undefined);
    return inherited === undefined ? { start: r.start, end: r.end } : { ...r, reason: inherited };
  });

  return [...retained, ...derived];
}

function committedReasonByCell(
  previous: readonly ReasonedRange[],
  visibleDays: readonly string[],
  tz: string,
): Map<CellKey, string | undefined> {
  const map = new Map<CellKey, string | undefined>();
  for (const block of previous) {
    const cells = rangesToCells([block], visibleDays, tz);
    for (const k of cells) map.set(k, block.reason);
  }
  return map;
}

function rangeCellKeys(range: InstantRange, visibleDays: readonly string[], tz: string): CellKey[] {
  const cells = rangesToCells([range], visibleDays, tz);
  return [...cells];
}
